import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Chip,
  Link as MuiLink,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserSettings } from './userSettingsSlice';
import {
  setTelegramBotToken,
  deleteTelegramBotToken,
} from '../../api/userSettings';
import { extractErrorMessage } from '../../api/extractErrorMessage';

/**
 * Telegram bot setup card for the Notifications tab.
 *
 * Per ADR-0001 each user owns their own bot (created via @BotFather).
 * This card walks them through the BotFather flow, validates the token
 * server-side via getMe, and renders the cached bot identity once linked.
 *
 * The token never round-trips back to the client after save: the GET
 * /v2/user/settings response strips it and emits the cached identity
 * (`telegramBot`) plus a `telegramBotTokenConfigured` flag. We refetch
 * settings after each mutation so the link/unlink state stays authoritative.
 */
const TelegramBotCard = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.userSettings.settings);
  const linkedBot = settings?.telegramBot || null;
  const isLinked = Boolean(linkedBot?.username || settings?.telegramBotTokenConfigured);

  const [tokenInput, setTokenInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [unlinkConfirming, setUnlinkConfirming] = useState(false);

  // Refetch error has to be reported separately from save/unlink errors
  // because fetchUserSettings rejects with a plain string (the slice's
  // thunk pre-runs extractErrorMessage and rejectWithValue's it). A plain
  // string has no `.response.data` / `.message`, so feeding it back into
  // extractErrorMessage falls through to the fallback — which would tell
  // the user the save/unlink itself failed. Splitting the try blocks
  // keeps each error message accurate to what actually went wrong.
  const REFETCH_FAILED_MESSAGE = 'Saved, but failed to refresh your settings — reload the page to confirm.';

  const handleSave = async (event) => {
    event.preventDefault();
    if (!tokenInput.trim()) {return;}
    setSubmitting(true);
    setError(null);
    try {
      await setTelegramBotToken(tokenInput.trim());
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save Telegram bot token'));
      setSubmitting(false);
      return;
    }
    // Save succeeded server-side. Clear the input + refresh the cached
    // identity so the linked-bot UI surfaces immediately. `force: true`
    // because the slice's 5-min cache would otherwise short-circuit.
    setTokenInput('');
    try {
      await dispatch(fetchUserSettings({ force: true })).unwrap();
    } catch {
      setError(REFETCH_FAILED_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlink = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await deleteTelegramBotToken();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to unlink Telegram bot'));
      setSubmitting(false);
      return;
    }
    // Unlink succeeded server-side. Same refetch + soft-failure pattern
    // as handleSave so a refetch hiccup doesn't claim the unlink failed.
    try {
      await dispatch(fetchUserSettings({ force: true })).unwrap();
      setUnlinkConfirming(false);
    } catch {
      setError(REFETCH_FAILED_MESSAGE);
      setUnlinkConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Telegram Bot
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Receive notifications via your own Telegram bot. We never use a
        shared bot — you control it and can revoke access anytime in
        BotFather.
      </Typography>

      {isLinked ? (
        <Box mt={2}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Chip color="success" size="small" label="Linked" />
            {linkedBot?.username && (
              <Typography variant="body2">
                <MuiLink
                  href={`https://t.me/${linkedBot.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  @{linkedBot.username}
                </MuiLink>
                {linkedBot.name ? ` — ${linkedBot.name}` : null}
              </Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Next step: open a chat with your bot and send any message
            (e.g. /start). Telegram will assign your chat a numeric id —
            paste it into a subscription below to receive notifications
            there.
          </Typography>
          {!unlinkConfirming ? (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setUnlinkConfirming(true)}
              disabled={submitting}
            >
              Unlink bot
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleUnlink}
                disabled={submitting}
              >
                {submitting ? 'Unlinking…' : 'Confirm unlink'}
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setUnlinkConfirming(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Stack>
          )}
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSave} mt={2}>
          <Typography variant="body2" component="div" mb={1}>
            <strong>One-time setup:</strong>
            <ol style={{ marginTop: 4, paddingLeft: 20 }}>
              <li>
                Open{' '}
                <MuiLink
                  href="https://t.me/BotFather"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  @BotFather
                </MuiLink>{' '}
                in Telegram and send <code>/newbot</code>.
              </li>
              <li>Pick a name and username (must end in <code>bot</code>).</li>
              <li>BotFather replies with an HTTP API token — paste it below.</li>
            </ol>
          </Typography>
          <TextField
            fullWidth
            label="Bot token"
            placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            type="password"
            autoComplete="off"
            disabled={submitting}
            size="small"
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting || !tokenInput.trim()}
          >
            {submitting ? 'Validating with Telegram…' : 'Save token'}
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default TelegramBotCard;
